import { Injectable, WritableSignal, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/browser';
import { AccountService } from '@wilson/account';
import { PayrollService } from '@wilson/api/gateway';
import {
  CorePayrollCategory,
  Overtime,
  OvertimeRangeConfig,
  OvertimesToSum,
  OvertimeSummary,
  OvertimeTimeRangeKeys,
  PayrollCategoryName,
  SumOfCategoriesWithTemporarySum,
  UserKpisInMinutes,
  YearMonth,
} from '@wilson/interfaces';
import { LocalizationState } from '@wilson/localization';
import { DateTimeFormat } from '@wilson/utils';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { PayrollCategoriesSummaryCache } from './payroll-categories-summary-cache.class';
import { format } from 'date-fns';
import { WorkTimeUtilizationsCache } from './work-time-utilizations-cache.class';
import {
  PdfTimesheetCache,
  PdfTimesheetData,
} from './pdf-timesheet-cache.class';
import { TranslateService } from '@ngx-translate/core';

const BONUSES_MASTER_DATA = [
  'consecutive-shifts-away-bonus',
  'dark-night-hours',
  'end-at-night-bonus',
  'etv1-bonus',
  'holiday-bonus',
  'night-hours',
  'overnight-shift-bonus',
  'overtime-hours-holiday',
  'overtime-hours-night',
  'overtime-hours-sunday',
  'overtime-hours-weekday',
  'per-diem',
  'saturday-hours',
  'shift-end-at-night',
  'shift-start-at-night',
  'shift-transfer-time',
  'shunting-bomlitz-bonus',
  'split-shift-bonus',
  'spontaneous-shift-bonus',
  'spontaneous-shift-time-bonus',
  'standby-bonus',
  'start-at-night-bonus',
  'sunday-bonus',
  'trainee-on-board-bonus',
  'travel-bonus',
  'unfavorable-shift-bonus',
  'overtime-compensation-bonus',
];

const WORKING_TIME_CATEGORIES: (PayrollCategoryName | 'overtimeInMinutes')[] = [
  PayrollCategoryName.IsWorkTime,
  PayrollCategoryName.PlannedWorktime,
];

export type WorkTimeBonus = SumOfCategoriesWithTemporarySum & { id: number };

@Injectable({
  providedIn: 'root',
})
export class WorkTimeManagementDataService {
  private readonly accountService = inject(AccountService);
  private readonly workTimeSummaryCache = new PayrollCategoriesSummaryCache();
  private readonly workTimeUtilizationsCache = new WorkTimeUtilizationsCache();
  private readonly pdfTimeSheetCache = new PdfTimesheetCache();
  private readonly store = inject(Store);
  private readonly payrollService = inject(PayrollService);
  private readonly translateService = inject(TranslateService);

  getWorktimeSummary(
    month: YearMonth,
    categories: PayrollCategoryName[] = [],
    refresh = false,
  ): Observable<Record<PayrollCategoryName, number>> {
    const dataSource = this.workTimeSummaryCache.get(month, refresh);
    return dataSource.pipe(
      map((payrollCategories) => {
        const categoryMap: Record<PayrollCategoryName, number> =
          categories.reduce(
            (acc, category) => {
              acc[category as PayrollCategoryName] = 0;
              return acc;
            },
            {} as Record<PayrollCategoryName, number>,
          );

        payrollCategories.forEach((category) =>
          category.sumOfCategories.forEach((result) => {
            categoryMap[result.name as PayrollCategoryName] +=
              result.name === PayrollCategoryName.IsWorkTime
                ? result.temporarySum || result.sum
                : result.sum;
          }),
        );
        return categoryMap;
      }),
    );
  }

  getWorktimeUtilizations(
    month: YearMonth,
    refresh = false,
  ): Observable<UserKpisInMinutes> {
    return this.workTimeUtilizationsCache.get(month, refresh);
  }

  getPdfTimesheetCache(
    month: YearMonth,
    refresh = false,
  ): Observable<PdfTimesheetData> {
    return this.pdfTimeSheetCache.get(month, refresh).pipe(
      map((fileAccess) => {
        const selectedYear = new Date(month).getFullYear();
        const selectedMonth = new Date(month).getMonth();
        return {
          fileAccess,
          fileName: `${this.translateService.instant('page.working_time.pdf_timesheet.title')} ${this.translateService.instant(`datetime.monthNames.${selectedMonth}`)} ${selectedYear}`,
        };
      }),
    );
  }

  getWorktimeBonusesSummary({
    month,
    isLoading,
    hasError,
    refresh = false,
  }: {
    month: YearMonth;
    isLoading: WritableSignal<boolean>;
    hasError: WritableSignal<boolean>;
    refresh?: boolean;
  }): Observable<WorkTimeBonus[]> {
    return this.getCategoriesSummaryByFilter(
      { month, isLoading, hasError, refresh },
      (categoryName) => BONUSES_MASTER_DATA.includes(categoryName),
    );
  }

  getOtherCategoriesSummary({
    month,
    isLoading,
    hasError,
    refresh = false,
  }: {
    month: YearMonth;
    isLoading: WritableSignal<boolean>;
    hasError: WritableSignal<boolean>;
    refresh?: boolean;
  }): Observable<WorkTimeBonus[]> {
    return this.getCategoriesSummaryByFilter(
      { month, isLoading, hasError, refresh },
      (categoryName) =>
        !BONUSES_MASTER_DATA.includes(categoryName) &&
        !WORKING_TIME_CATEGORIES.includes(categoryName),
    );
  }

  private getCategoriesSummaryByFilter(
    {
      month,
      isLoading,
      hasError,
      refresh = false,
    }: {
      month: YearMonth;
      isLoading: WritableSignal<boolean>;
      hasError: WritableSignal<boolean>;
      refresh?: boolean;
    },
    includeCategory: (
      categoryName: PayrollCategoryName | 'overtimeInMinutes',
    ) => boolean,
  ): Observable<WorkTimeBonus[]> {
    isLoading.set(true);
    hasError.set(false);
    const dataSource = this.workTimeSummaryCache.get(month, refresh, {
      onFallbackToCache: () => hasError.set(true),
    });
    return dataSource.pipe(
      map((payrollCategories) => {
        const categories: WorkTimeBonus[] = [];
        const nameDescriptor = this.store.selectSnapshot(
          LocalizationState.getEntityLang,
        );

        if (payrollCategories.length) {
          payrollCategories[0].sumOfCategories.forEach((category, index) => {
            if (includeCategory(category.name)) {
              categories.push({
                ...category,
                id: index,
                sum: 0,
                temporarySum: 0,
                wageReportUnit: category.wageReportUnit,
                translatedName:
                  category[nameDescriptor] || category.nameEn || '',
              });
            }
          });

          payrollCategories.forEach((payrollCategory) =>
            payrollCategory.sumOfCategories.forEach((category, index) => {
              if (includeCategory(category.name)) {
                const itemIndex = categories.findIndex(
                  (item) => item.id === index,
                );
                categories[itemIndex].sum += category.sum;
                if (
                  categories[itemIndex].temporarySum !== undefined &&
                  category?.temporarySum !== undefined
                ) {
                  categories[itemIndex].temporarySum += category.temporarySum;
                }
                categories[itemIndex].wageReportUnit = category.wageReportUnit;
              }
            }),
          );
        }

        return categories.sort((a, b) => b.sum - a.sum);
      }),
      catchError((error) => {
        captureException(error);
        hasError.set(true);
        return of([]);
      }),
      finalize(() => isLoading.set(false)),
    );
  }

  getOvertimeSummaryByRanges(
    userId: string,
    config: Partial<Record<OvertimeTimeRangeKeys, OvertimeRangeConfig>>,
  ): Observable<Partial<Record<OvertimeTimeRangeKeys, OvertimeSummary>>> {
    const rangeConfig = Object.entries(config) as [
      OvertimeTimeRangeKeys,
      OvertimeRangeConfig,
    ][];
    const allTimeRanges = Object.values(config).map((c) => ({
      start: format(
        new Date(c!.timeRange.start),
        DateTimeFormat.DatabaseDateFormat,
      ),
      end: format(
        new Date(c!.timeRange.end),
        DateTimeFormat.DatabaseDateFormat,
      ),
    }));
    const payrollCategories$ = this.payrollService.getPayrollCategories();

    return combineLatest([
      payrollCategories$,
      this.accountService.getUserOvertimes(allTimeRanges, {
        where: { id: userId },
      }),
    ]).pipe(
      map(([payrollCategories, userOvertimesFromBackend]) => {
        const overtimes: Overtime[] = [];
        for (const userOvertime of userOvertimesFromBackend) {
          overtimes.push(...userOvertime.overtimes);
        }
        return this.buildSummaryByRanges(
          rangeConfig,
          overtimes,
          payrollCategories,
        );
      }),
    );
  }

  private buildSummaryByRanges(
    rangeConfig: [OvertimeTimeRangeKeys, OvertimeRangeConfig][],
    overtimesFromBackend: Overtime[],
    payrollCategories: CorePayrollCategory[],
  ): Partial<Record<OvertimeTimeRangeKeys, OvertimeSummary>> {
    const result: Partial<Record<OvertimeTimeRangeKeys, OvertimeSummary>> = {};

    for (const [key, config] of rangeConfig) {
      const { start, end } = config.timeRange;
      const formattedStart = format(
        new Date(start),
        DateTimeFormat.DatabaseDateFormat,
      );
      const formattedEnd = format(
        new Date(end),
        DateTimeFormat.DatabaseDateFormat,
      );

      const overtime = overtimesFromBackend.find(
        (backendOvertime) =>
          format(
            new Date(backendOvertime.timeRange.start),
            DateTimeFormat.DatabaseDateFormat,
          ) === formattedStart &&
          format(
            new Date(backendOvertime.timeRange.end),
            DateTimeFormat.DatabaseDateFormat,
          ) === formattedEnd,
      );

      const summaryMap: OvertimeSummary = {};

      for (const field of config.fields) {
        if (field === OvertimesToSum.RestOfOvertimes) {
          summaryMap[field] = overtime
            ? this.extractOvertimeByPayrollCategoryNames(
                payrollCategories,
                overtime,
              )
            : [];
        } else {
          summaryMap[field] = overtime ? overtime[field] : 0;
        }
      }

      result[key] = summaryMap;
    }
    return result;
  }

  private extractOvertimeByPayrollCategoryNames(
    payrollCategories: CorePayrollCategory[],
    overtime: Overtime,
  ): (CorePayrollCategory & {
    value: number;
  })[] {
    const result: (CorePayrollCategory & { value: number })[] = [];
    const nameDescriptor = this.store.selectSnapshot(
      LocalizationState.getEntityLang,
    );

    for (const [categoryName, value] of Object.entries(
      overtime.overtTimeByPayrollCategoryNames,
    )) {
      const payrollCategory = payrollCategories.find(
        (el) => el.name === categoryName,
      );

      if (payrollCategory) {
        result.push({
          ...payrollCategory,
          translatedName:
            payrollCategory[nameDescriptor] || payrollCategory.nameEn || '',
          value,
        });
      }
    }
    return result;
  }
}
